import type { TextRange } from "@/lib/client/generated/TypeDefinitions.ts"
import type { MarkerType } from "@/lib/diff/markerType.ts"

export class LineMarker {
  constructor(
    readonly type: MarkerType,
    readonly range: TextRange,
    readonly navigationToken: string | null = null,
    readonly identifier: string | null = null,
    readonly isHighlighted: boolean = false
  ) {
  }
}

export abstract class ClosedRange<T> {
  constructor(readonly start: T, readonly endInclusive: T) {
  }

  contains(value: T) {
    return value >= this.start && value <= this.endInclusive
  }

  isEmpty() {
    return this.start > this.endInclusive
  }
}

export class IntRange extends ClosedRange<number> {
  get size() {
    return this.endInclusive - this.start + 1
  }

  minus(value: IntRange) {
    const ranges: IntRange[] = []

    if (value.contains(this.start) && value.contains(this.endInclusive)) {
      return ranges
    }

    if (value.start > this.endInclusive || this.start > value.endInclusive) {
      return [this]
    }

    if (range(this.start + 1, this.endInclusive).contains(value.start)) {
      ranges.push(range(this.start, Math.min(value.start - 1, this.endInclusive)))
    }

    if (range(this.start, this.endInclusive - 1).contains(value.endInclusive)) {
      ranges.push(range(Math.max(this.start, value.endInclusive + 1), this.endInclusive))
    }

    return ranges
  }

  firstOrNull(predicate: (value: number) => boolean): number | null {
    for (let i = this.start; i <= this.endInclusive; i++) {
      if (predicate(i)) {
        return i
      }
    }

    return null
  }
}

export function range(start: number, endInclusive: number) {
  return new IntRange(start, endInclusive)
}

interface Comparable<T> {
  compareTo(other: T): number
}

export class SortedList<T extends Comparable<T>> {
  private list: T[] = []
  private sorted = false

  add(item: T): void {
    if (this.sorted && (this.list.length === 0 || this.list[this.list.length - 1] >= item)) {
      this.list.push(item)
    } else {
      this.list.push(item)
      this.sorted = false
    }
  }

  addAll(items: Iterable<T>): void {
    this.list.push(...items)
    this.sorted = false
  }

  remove(): T {
    if (!this.sorted) {
      this.list.sort((a, b) => b.compareTo(a))
      this.sorted = true
    }

    if (this.list.length === 0) {
      throw new Error("NoSuchElementException")
    }

    return this.list.pop()!
  }

  isEmpty(): boolean {
    return this.list.length === 0
  }
}

export class TextRangeImpl {
  constructor(public start: number, public length: number) {
  }

  get end() {
    return this.start + this.length
  }

  isEmpty() {
    return this.length <= 0
  }

  isNotEmpty() {
    return !this.isEmpty()
  }

  static from(data: { start: number, length: number }) {
    return new TextRangeImpl(data.start, data.length)
  }
}